import { useEffect, useMemo, useRef, useState } from "react";
import { initialLeftMonth } from "@/lib/date-range-view";
import { useLayer } from "@/lib/layer-stack";
import { I } from "@/components/Icon";
import {
  addDays,
  rangeFromPreset,
  startOfWeekMon,
  type DateRange,
  type RangeMode,
  type RangePreset,
} from "@/lib/date-range";

// Visible preset list — matches the Claude Design handoff (handoff-4).
// `4w`, `12w`, `ytd` still resolve in rangeFromPreset for backward
// compat with bookmarked URLs / saved views, just not surfaced here.
const RANGE_OPTIONS: { id: RangePreset; label: string }[] = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "7d", label: "Last 7 days" },
  { id: "14d", label: "Last 14 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "3mo", label: "Last 3 months" },
  { id: "last_mo", label: "Last month" },
  { id: "last_week", label: "Last week" },
  { id: "this_mo", label: "This month" },
  { id: "this_week", label: "This week" },
];

const MO = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December",
];

function addMonths(d: Date, n: number) {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
}
function sameDay(a: Date | null, b: Date | null) {
  return !!a && !!b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}
function inRange(d: Date, a: Date | null, b: Date | null) {
  if (!a || !b) return false;
  const t = d.getTime();
  return t >= Math.min(a.getTime(), b.getTime()) && t <= Math.max(a.getTime(), b.getTime());
}
function fmtDayMonth(d: Date | null) {
  if (!d) return "";
  return `${d.getDate()} ${MO[d.getMonth()]!.slice(0, 3)}`;
}
function fmtDisplay(a: Date | null, b: Date | null) {
  if (!a) return "Select period";
  const am = MO[a.getMonth()]!.slice(0, 3);
  if (!b || sameDay(a, b)) return `${am} ${a.getDate()}`;
  const bm = MO[b.getMonth()]!.slice(0, 3);
  if (a.getMonth() === b.getMonth() && a.getFullYear() === b.getFullYear())
    return `${am} ${a.getDate()} – ${b.getDate()}`;
  return `${am} ${a.getDate()} – ${bm} ${b.getDate()}`;
}

function MonthGrid({
  month, start, end, hover, onPick, onHover, onStep, stepDir,
}: {
  month: Date;
  start: Date | null;
  end: Date | null;
  hover: Date | null;
  onPick: (d: Date) => void;
  onHover: (d: Date | null) => void;
  onStep: (n: number) => void;
  stepDir: "left" | "right" | "both";
}) {
  const year = month.getFullYear();
  const m = month.getMonth();
  const first = new Date(year, m, 1);
  const gridStart = startOfWeekMon(first);
  const days: Date[] = [];
  for (let i = 0; i < 42; i++) days.push(addDays(gridStart, i));
  const effEnd = end || hover;
  return (
    <div style={{ width: 240 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 2px 10px" }}>
        {stepDir === "left" || stepDir === "both" ? (
          <button className="dp-nav" onClick={() => onStep(-1)} aria-label="Previous month">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m15 18-6-6 6-6" />
            </svg>
          </button>
        ) : (
          <span style={{ width: 28 }} />
        )}
        <div style={{ fontSize: 13.5, fontWeight: 500, color: "var(--c-text)" }}>
          {MO[m]} {year}
        </div>
        {stepDir === "right" || stepDir === "both" ? (
          <button className="dp-nav" onClick={() => onStep(1)} aria-label="Next month">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m9 18 6-6-6-6" />
            </svg>
          </button>
        ) : (
          <span style={{ width: 28 }} />
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0, fontSize: 11, color: "var(--c-text-4)", padding: "0 2px 6px", textAlign: "center" }}>
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <div key={d} style={{ padding: "2px 0", fontWeight: 500 }}>{d}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0 }}>
        {days.map((d, i) => {
          const outside = d.getMonth() !== m;
          const isStart = start && sameDay(d, start);
          const isEnd = end && sameDay(d, end);
          const isHover = !end && hover && sameDay(d, hover);
          const within = start && effEnd && inRange(d, start, effEnd) && !isStart && !isEnd;
          const edge = isStart || isEnd || isHover;
          if (outside) {
            return <div key={i} className="dp-cell" data-outside="1" />;
          }
          return (
            <button
              key={i}
              onClick={() => onPick(d)}
              onMouseEnter={() => onHover(d)}
              className="dp-cell"
              data-edge={edge ? "1" : undefined}
              data-within={within ? "1" : undefined}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const MODE_OPTIONS: { id: RangeMode; name: string; desc: string }[] = [
  { id: "static",  name: "Static dates",   desc: "The dates are fixed." },
  { id: "rolling", name: "Rolling period", desc: "As the calendar changes, the dates are updated." },
];

function ModeDropdown({
  value,
  onChange,
}: {
  value: RangeMode;
  onChange: (m: RangeMode) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useLayer(open, () => setOpen(false));
  const cur = MODE_OPTIONS.find((m) => m.id === value) || MODE_OPTIONS[1]!;
  return (
    <div className="dp-mode-wrap" ref={ref}>
      <button
        className="dp-mode-trigger"
        data-open={open ? "1" : undefined}
        onClick={() => setOpen((o) => !o)}
        type="button"
      >
        <span>{cur.name}</span>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="dp-mode-menu" role="listbox">
          {MODE_OPTIONS.map((m) => (
            <button
              key={m.id}
              className="dp-mode-item"
              data-active={m.id === value ? "1" : undefined}
              onClick={() => {
                onChange(m.id);
                setOpen(false);
              }}
              type="button"
            >
              <span className="dp-mode-check" aria-hidden="true">
                {m.id === value ? (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                  </svg>
                ) : null}
              </span>
              <span className="dp-mode-name">{m.name}</span>
              <span className="dp-mode-desc">{m.desc}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function DateRangePicker({
  preset,
  range,
  mode = "rolling",
  onChange,
}: {
  preset: RangePreset;
  range: DateRange;
  /** Optional — older callers without rolling/static support default
   *  to "rolling", which matches the natural reading of preset names. */
  mode?: RangeMode;
  onChange: (next: { preset: RangePreset; range: DateRange; mode: RangeMode }) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  useLayer(open, () => setOpen(false));

  // Draft: edits stay local until Apply. Cancel discards. Matches the
  // handoff's explicit footer — preset clicks no longer auto-commit.
  const [draftStart, setDraftStart] = useState<Date | null>(range.from);
  const [draftEnd, setDraftEnd] = useState<Date | null>(range.to);
  const [draftPreset, setDraftPreset] = useState<RangePreset>(preset);
  const [draftMode, setDraftMode] = useState<RangeMode>(mode);
  const [hover, setHover] = useState<Date | null>(null);
  // Calendar viewport opens on the committed range (`initialLeftMonth`):
  // up to 30 days — its start month on the left; longer — the month it
  // ends in. Per owner 2026-10-07 (was always [previous | current] month).
  const [leftMonth, setLeftMonth] = useState<Date>(() => initialLeftMonth(range.from, range.to));
  const rightMonth = useMemo(() => addMonths(leftMonth, 1), [leftMonth]);

  // Re-sync draft on (re)open so the popover always opens to the
  // currently committed state, regardless of what the user did and
  // then cancelled last time. Calendar viewport snaps to the committed
  // range every open.
  useEffect(() => {
    if (open) {
      setDraftStart(range.from);
      setDraftEnd(range.to);
      setDraftPreset(preset);
      setDraftMode(mode);
      setLeftMonth(initialLeftMonth(range.from, range.to));
      setHover(null);
    }
  }, [open, range.from, range.to, preset, mode]);

  const onPick = (d: Date) => {
    if (!draftStart || (draftStart && draftEnd)) {
      setDraftStart(d);
      setDraftEnd(null);
      setHover(d);
    } else if (d < draftStart) {
      setDraftEnd(draftStart);
      setDraftStart(d);
    } else {
      setDraftEnd(d);
    }
    // Manual calendar pick → custom range. The mode dropdown still
    // works (user can mark a custom span as Static or Rolling), even
    // though for custom we can't auto-roll on reload — see the lib
    // comment on RangeMode.
    setDraftPreset("custom");
  };

  const applyPreset = (id: RangePreset) => {
    const r = rangeFromPreset(id, new Date());
    setDraftStart(r.from);
    setDraftEnd(r.to);
    setDraftPreset(id);
    // Don't reposition the calendar viewport inside an open popover —
    // it is placed once, on open (see the effect above). Highlights
    // still render when the picked range runs past the visible months.
  };

  const cancel = () => {
    setOpen(false);
  };

  const apply = () => {
    if (!draftStart) {
      setOpen(false);
      return;
    }
    const end = draftEnd || draftStart;
    onChange({
      preset: draftPreset,
      range: { from: draftStart, to: end },
      mode: draftMode,
    });
    setOpen(false);
  };

  const triggerMain = fmtDisplay(range.from, range.to);
  const triggerSub = preset === "custom"
    ? "Custom"
    : RANGE_OPTIONS.find((o) => o.id === preset)?.label
      // Legacy preset bookmark (4w / 12w / ytd) — fall back to a
      // generic label since they're hidden from the visible list.
      || (preset === "ytd" ? "Year to Date"
        : preset === "12w" ? "Last 12 weeks"
        : preset === "4w"  ? "Last 4 weeks"
        : "Custom");

  const summary = (draftStart && draftEnd)
    ? `${fmtDayMonth(draftStart)} – ${fmtDayMonth(draftEnd)}`
    : (draftStart ? fmtDayMonth(draftStart) : "Select period");

  return (
    <div ref={ref} style={{ position: "relative", display: "inline-block" }}>
      <button onClick={() => setOpen((o) => !o)} className="range-picker" data-open={open}>
        <span className="range-ico">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M3 10h18" />
            <path d="M8 3v4" />
            <path d="M16 3v4" />
          </svg>
        </span>
        <span className="range-meta">
          <span className="range-dates">{triggerMain}</span>
          <span className="range-sub">{triggerSub}</span>
        </span>
        <span className="app-chev">{I.chevronDown}</span>
      </button>
      {open && (
        <div className="dp-popover" onMouseLeave={() => setHover(null)}>
          <div className="dp-presets">
            {RANGE_OPTIONS.map((o) => (
              <button
                key={o.id}
                className="dp-preset"
                data-active={draftPreset === o.id ? "1" : undefined}
                onClick={() => applyPreset(o.id)}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="dp-cal">
            <div className="dp-grids">
              <MonthGrid
                month={leftMonth}
                start={draftStart}
                end={draftEnd}
                hover={hover}
                onPick={onPick}
                onHover={setHover}
                onStep={(n) => setLeftMonth(addMonths(leftMonth, n))}
                stepDir="left"
              />
              <MonthGrid
                month={rightMonth}
                start={draftStart}
                end={draftEnd}
                hover={hover}
                onPick={onPick}
                onHover={setHover}
                onStep={(n) => setLeftMonth(addMonths(leftMonth, n))}
                stepDir="right"
              />
            </div>
            <div className="dp-foot">
              <ModeDropdown value={draftMode} onChange={setDraftMode} />
              <div className="dp-foot-right">
                <span className="dp-foot-summary">{summary}</span>
                <button className="btn" onClick={cancel} type="button">Cancel</button>
                <button className="btn primary" onClick={apply} type="button">Apply</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
